import { useTranslation } from "react-i18next";

import type { CommercialPreview } from "@/api/commercialCalibration";
import { formatBrl } from "@/components/projects/diagnostic/diagnosticData";
import { cn } from "@/lib/utils";

/**
 * O quadro "Incentivos" (EF-FUNC-012 v0.1 RF-04), da prévia do servidor. Sem
 * parâmetros na faixa a prévia não tem incentivos, e as linhas ficam em traço.
 */
export function IncentivesCard({
  softwareTcv,
  commissionPct,
  incentives,
}: {
  softwareTcv: number | null;
  commissionPct: number | null;
  incentives: CommercialPreview["incentives"] | undefined;
}) {
  const { t } = useTranslation();
  const money = (value: number | null | undefined) => (value == null ? "—" : formatBrl(value));
  const rows = [
    { key: "tcv", label: t("SAP software (TCV)"), value: money(softwareTcv) },
    {
      key: "after-commission",
      label: t("Software − commission ({{pct}}%)", { pct: commissionPct ?? "—" }),
      value: money(incentives?.software_after_commission),
    },
    {
      key: "cs",
      label: "Consider Selection (C&S)",
      value: money(incentives?.consider_selection_amount),
    },
    {
      key: "ao",
      label: "Adopt Operation (A&O)",
      value: money(incentives?.adopt_operation_amount),
    },
    { key: "hyperscale", label: "Hyperscale", value: money(incentives?.hyperscale_amount) },
  ];

  return (
    <section
      data-testid="commercial-incentives"
      className="flex flex-col"
    >
      <h3 className="mb-1.5 font-mono text-[10.5px] uppercase tracking-[0.1em] text-[#5a6b86]">
        {t("Incentives (project base)")}
      </h3>
      {rows.map((row) => (
        <Row key={row.key} label={row.label} value={row.value} />
      ))}
      <Row label={t("Total incentives")} value={money(incentives?.total_incentives)} total />
    </section>
  );
}

function Row({ label, value, total = false }: { label: string; value: string; total?: boolean }) {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 py-1 text-sm",
        total ? "mt-1 font-semibold text-[#08254c]" : "border-b border-dashed border-[#eef1f6]",
      )}
    >
      <span>{label}</span>
      <span className="font-mono tabular-nums">{value}</span>
    </div>
  );
}
